// @ts-nocheck
/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect, useMemo, useState} from 'react'
import axios from 'axios'
import clsx from 'clsx'
import {useAuth} from '../../modules/auth'
import {getAdminToken} from '../../modules/admin/auth/_requests'
import './index.scss'

const API_URL = process.env.REACT_APP_API_URL

type PostRow = {
  _id: string
  name?: string
  post_content?: string
  campaign_name?: string
  today_count: number
  week_count: number
  month_count: number
  total_count: number
  last_published_at?: string
}

type Totals = {
  today: number
  today_scheduled?: number
  week: number
  month: number
  lifetime: number
}

type Instance = {
  _id: string
  published_at: string
  group_name?: string
  group_id?: string
  facebook_post_id?: string
  post_url?: string
  post_url_id?: string
  profile_id?: string
  profile_parent_id?: string
  profile_first_name?: string
  profile_last_name?: string
  profile_facebook_id?: string
  profile_picture_url?: string
  verification_status?: 'pending' | 'verified' | 'deleted' | 'error'
  is_xpost?: boolean
}

const profileName = (inst: Instance) => {
  const full = [inst.profile_first_name, inst.profile_last_name].filter(Boolean).join(' ').trim()
  return full || inst.profile_facebook_id || '—'
}

const profileInitial = (inst: Instance) => {
  const n = profileName(inst)
  return n && n !== '—' ? n.charAt(0).toUpperCase() : '?'
}

const fbProfileUrl = (inst: Instance) =>
  inst.profile_facebook_id ? `https://www.facebook.com/${inst.profile_facebook_id}` : undefined

const fmtDate = (iso?: string) => {
  if (!iso) return '—'
  const d = new Date(iso)
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const yyyy = d.getFullYear()
  const hh = String(d.getHours()).padStart(2, '0')
  const mi = String(d.getMinutes()).padStart(2, '0')
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`
}

const truncate = (s: string, n = 80) => (s && s.length > n ? s.slice(0, n).trim() + '…' : s || '')

const fbGroupUrl = (groupId?: string) => (groupId ? `https://www.facebook.com/groups/${groupId}/` : undefined)

// Facebook's "Published" tab for your own posts inside a group. Used for
// cross-posted (xpost) instances, which have no per-post permalink.
const fbGroupPublishedUrl = (groupId?: string) =>
  groupId ? `https://www.facebook.com/groups/${groupId}/my_posted_content` : undefined

// Resolve the link for an instance — always the post, never the group.
// Prefer the canonical /groups/<gid>/posts/<fbid>/ form (matches FB's modern
// permalink). Fall back to the stored post_url (legacy /permalink/<id>/ form
// or pending_posts URL) when we don't have both ids. Returns undefined only
// when there is no usable post identifier at all.
const fbPostUrl = (inst: Instance) => {
  if (inst.group_id && inst.facebook_post_id) {
    return `https://www.facebook.com/groups/${inst.group_id}/posts/${inst.facebook_post_id}/`
  }
  if (inst.post_url) return inst.post_url
  if (inst.group_id && inst.post_url_id) {
    return `https://www.facebook.com/groups/${inst.group_id}/permalink/${inst.post_url_id}/`
  }
  return undefined
}

// Admin-only: open the profile's live browser preview in a popup and
// auto-navigate it to the post/group URL (?goto=). Mirrors the profile-preview
// popup used on the groups page; the fleet server drives the bot's real browser.
const PREVIEW_BASE = 'https://pp234234.fbzipper.com'
// sessionProfileId = the profile whose bot session/browser to open (the PARENT for a
// sub-profile). switchToFbId = the sub-profile's facebook_id to switch the actor to
// (?as=) before the ?goto= navigation, so the post opens under the right profile.
const openLivePreview = (sessionProfileId?: string, gotoUrl?: string, switchToFbId?: string) => {
  if (!sessionProfileId) return
  const params: string[] = []
  if (gotoUrl) params.push(`goto=${encodeURIComponent(gotoUrl)}`)
  if (switchToFbId) params.push(`as=${encodeURIComponent(switchToFbId)}`)
  const q = params.length ? `?${params.join('&')}` : ''
  window.open(
    `${PREVIEW_BASE}/preview/${sessionProfileId}/${q}`,
    `profile_preview_${sessionProfileId}`,
    'width=1100,height=820,resizable=yes,scrollbars=yes'
  )
}

const StatsWrapper: FC = () => {
  const {auth} = useAuth()
  const [rows, setRows] = useState<PostRow[] | null>(null)
  const [totals, setTotals] = useState<Totals | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Record<string, {loading: boolean; instances?: Instance[]; error?: string}>>({})
  const [search, setSearch] = useState('')
  const [isAdmin, setIsAdmin] = useState(false)

  // Admin gate for the live-preview button (same check used on groups/contacts).
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const adminToken = getAdminToken()
        const headers: Record<string, string> = {}
        if (adminToken) headers['Authorization'] = `Bearer ${adminToken}`
        const res = await axios.get(`${API_URL}/admin/check-auth`, {headers, withCredentials: true})
        if (!cancelled) setIsAdmin(res.data.loggedIn === true)
      } catch {
        if (!cancelled) setIsAdmin(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setLoading(true)
        setError(null)
        const res = await axios.post(`${API_URL}/post-stats`, {api_token: auth?.api_token || ''})
        if (!cancelled) {
          setRows(res.data.posts || [])
          setTotals(res.data.totals || {today: 0, week: 0, month: 0, lifetime: 0})
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.response?.data?.error || e?.message || 'Error')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [auth?.api_token])

  const filteredRows = useMemo(() => {
    if (!rows) return []
    const q = search.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) =>
      (r.name || '').toLowerCase().includes(q) ||
      (r.post_content || '').toLowerCase().includes(q) ||
      (r.campaign_name || '').toLowerCase().includes(q)
    )
  }, [rows, search])

  const toggleExpand = async (postId: string) => {
    const cur = expanded[postId]
    if (cur && !cur.error) {
      // collapse
      setExpanded((s) => {
        const next = {...s}
        delete next[postId]
        return next
      })
      return
    }
    setExpanded((s) => ({...s, [postId]: {loading: true}}))
    try {
      const res = await axios.post(`${API_URL}/post-stats/${postId}/instances`, {api_token: auth?.api_token || ''})
      setExpanded((s) => ({...s, [postId]: {loading: false, instances: res.data.instances || []}}))
    } catch (e: any) {
      setExpanded((s) => ({...s, [postId]: {loading: false, error: e?.response?.data?.error || e?.message || 'Error'}}))
    }
  }

  if (loading) {
    return (
      <div className='stats-page-v2' dir='rtl'>
        <div className='stats-loading'>
          <div className='loading-spinner' />
          <div>טוען נתונים...</div>
        </div>
      </div>
    )
  }
  if (error) {
    return (
      <div className='stats-page-v2' dir='rtl'>
        <div className='stats-empty'>
          <div className='empty-icon' style={{fontSize: 36}}>⚠️</div>
          <div className='empty-title' style={{fontWeight: 700, marginTop: 8}}>שגיאה בטעינת הנתונים</div>
          <div className='empty-text' style={{marginTop: 4, color: '#888'}}>{error}</div>
        </div>
      </div>
    )
  }

  return (
    <div className='stats-page-v2' dir='rtl'>
      <div className='page-header'>
        <h1 className='page-title'>פוסטים שפורסמו</h1>
        <p className='page-subtitle'>כל פוסט עם כמות הפרסומים שלו היום ובשבוע. לחץ על שורה כדי לראות את הקישורים לפרסומים בפועל.</p>
      </div>

      {totals && (
        <section className='ps-summary'>
          <div className='ps-stat ps-stat-blue'>
            <div className='ps-stat-icon'>
              <svg viewBox='0 0 24 24' width='22' height='22' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                <path d='M3 3v18h18'/><path d='M18 17V9'/><path d='M13 17V5'/><path d='M8 17v-3'/>
              </svg>
            </div>
            <div className='ps-stat-body'>
              <div className='ps-stat-label'>סה״כ אי פעם</div>
              <div className='ps-stat-value'>{totals.lifetime.toLocaleString()}</div>
              <div className='ps-stat-sub'>פוסטים מאז שהצטרפת</div>
            </div>
          </div>
          <div className='ps-stat ps-stat-purple'>
            <div className='ps-stat-icon'>
              <svg viewBox='0 0 24 24' width='22' height='22' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                <rect width='18' height='18' x='3' y='4' rx='2'/><path d='M16 2v4'/><path d='M8 2v4'/><path d='M3 10h18'/>
              </svg>
            </div>
            <div className='ps-stat-body'>
              <div className='ps-stat-label'>החודש האחרון</div>
              <div className='ps-stat-value'>{totals.month.toLocaleString()}</div>
              <div className='ps-stat-sub'>30 ימים אחרונים</div>
            </div>
          </div>
          <div className='ps-stat ps-stat-green'>
            <div className='ps-stat-icon'>
              <svg viewBox='0 0 24 24' width='22' height='22' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                <polyline points='22 7 13.5 15.5 8.5 10.5 2 17'/><polyline points='16 7 22 7 22 13'/>
              </svg>
            </div>
            <div className='ps-stat-body'>
              <div className='ps-stat-label'>השבוע</div>
              <div className='ps-stat-value'>{totals.week.toLocaleString()}</div>
              <div className='ps-stat-sub'>7 ימים אחרונים</div>
            </div>
          </div>
          <div className='ps-stat ps-stat-amber'>
            <div className='ps-stat-icon'>
              <svg viewBox='0 0 24 24' width='22' height='22' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                <path d='M8 2v4'/><path d='M16 2v4'/><rect width='18' height='18' x='3' y='4' rx='2'/><path d='M3 10h18'/>
                <path d='M8 14h.01'/><path d='M12 14h.01'/><path d='M16 14h.01'/>
              </svg>
            </div>
            <div className='ps-stat-body'>
              <div className='ps-stat-label'>היום</div>
              {totals.today_scheduled != null && totals.today_scheduled > 0 ? (
                <>
                  <div className='ps-stat-value'>
                    {totals.today.toLocaleString()}
                    <span className='ps-stat-of'> / {totals.today_scheduled.toLocaleString()}</span>
                  </div>
                  <div className='ps-stat-progress'>
                    <div className='ps-stat-progress-bar' style={{width: `${Math.min(100, Math.round((totals.today / totals.today_scheduled) * 100))}%`}} />
                  </div>
                  <div className='ps-stat-sub'>
                    פורסמו מתוך {totals.today_scheduled.toLocaleString()} מתוזמנים ({Math.round((totals.today / totals.today_scheduled) * 100)}%)
                  </div>
                </>
              ) : (
                <>
                  <div className='ps-stat-value'>{totals.today.toLocaleString()}</div>
                  <div className='ps-stat-sub'>פוסטים שפורסמו היום</div>
                </>
              )}
            </div>
          </div>
        </section>
      )}

      <section className='card'>
        <div className='ps-toolbar'>
          <div className='ps-search'>
            <svg viewBox='0 0 24 24' width='16' height='16' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
              <circle cx='11' cy='11' r='8'/><path d='m21 21-4.3-4.3'/>
            </svg>
            <input
              type='text'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder='חיפוש לפי שם פוסט, תוכן או קמפיין...'
            />
            {search && (
              <button type='button' className='ps-search-clear' onClick={() => setSearch('')} aria-label='נקה'>×</button>
            )}
          </div>
          <div className='ps-count-pill'>
            <span className='dot' />
            {filteredRows.length} {search ? `מתוך ${rows?.length || 0}` : ''} פוסטים
          </div>
        </div>
        {(!rows || rows.length === 0) ? (
          <div style={{padding: '2rem 0', color: 'rgba(28,25,52,0.5)', textAlign: 'center'}}>אין פוסטים שפורסמו עדיין</div>
        ) : filteredRows.length === 0 ? (
          <div style={{padding: '2rem 0', color: 'rgba(28,25,52,0.5)', textAlign: 'center'}}>לא נמצאו פוסטים תואמים לחיפוש</div>
        ) : (
          <div className='post-stats-table-wrap'>
            <table className='post-stats-table' dir='rtl'>
              <thead>
                <tr>
                  <th style={{width: 44}}></th>
                  <th>פוסט</th>
                  <th>קמפיין</th>
                  <th style={{width: 70}}>היום</th>
                  <th style={{width: 70}}>השבוע</th>
                  <th style={{width: 70}}>החודש</th>
                  <th style={{width: 90}}>סה״כ</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => {
                  const isOpen = !!expanded[r._id]
                  const exp = expanded[r._id]
                  return (
                    <>
                      <tr key={r._id} className={clsx('post-row', isOpen && 'open')} onClick={() => toggleExpand(r._id)}>
                        <td className='chev-cell'>
                          <span className={clsx('chev-btn', isOpen && 'open')} title={isOpen ? 'סגור' : 'הצג פרסומים'}>
                            <span className='chev-arrow'>▾</span>
                          </span>
                        </td>
                        <td>
                          <div className='post-name'>{r.name || truncate(r.post_content || '', 60) || '—'}</div>
                          {r.name && r.post_content ? <div className='post-snippet'>{truncate(r.post_content, 80)}</div> : null}
                          <div className='post-expand-hint'>
                            {isOpen ? 'לחץ לסגירה' : `לחץ לצפייה ב-${r.total_count.toLocaleString()} פרסומים`}
                          </div>
                        </td>
                        <td><span className='campaign-tag'>{r.campaign_name || '—'}</span></td>
                        <td className='num'>{r.today_count.toLocaleString()}</td>
                        <td className='num'>{r.week_count.toLocaleString()}</td>
                        <td className='num'>{(r.month_count ?? 0).toLocaleString()}</td>
                        <td className='num strong'>{r.total_count.toLocaleString()}</td>
                      </tr>
                      {isOpen && (
                        <tr key={r._id + '-exp'} className='exp-row'>
                          <td colSpan={7}>
                            {exp?.loading ? (
                              <div className='exp-loading'>טוען פרסומים...</div>
                            ) : exp?.error ? (
                              <div className='exp-error'>שגיאה: {exp.error}</div>
                            ) : !exp?.instances || exp.instances.length === 0 ? (
                              <div className='exp-empty'>אין פרסומים</div>
                            ) : (
                              <div className='inst-list'>
                                {exp.instances.map((inst) => {
                                  // Cross-posts have no per-post permalink — link to the
                                  // group's "Published" tab instead.
                                  const postUrl = inst.is_xpost
                                    ? (fbGroupPublishedUrl(inst.group_id) || fbPostUrl(inst))
                                    : fbPostUrl(inst)
                                  return (
                                    <div className='inst-cell' key={inst._id}>
                                      <div className='inst-row'>
                                        <span className='inst-date'>{fmtDate(inst.published_at)}</span>
                                        <span className='inst-profile' title={profileName(inst)}>
                                          <span className='inst-avatar' aria-label={profileName(inst)}>
                                            {inst.profile_picture_url ? (
                                              <img src={inst.profile_picture_url} alt={profileName(inst)} />
                                            ) : (
                                              <span className='inst-avatar-fallback'>{profileInitial(inst)}</span>
                                            )}
                                          </span>
                                        </span>
                                        <span className='inst-group'>
                                          {inst.group_name || inst.group_id || '—'}
                                          {inst.is_xpost && <span className='inst-xpost-badge' title='הופץ דרך הפצה מרובת קבוצות'>הפצה</span>}
                                        </span>
                                        <span className='inst-status'>נשלח</span>
                                        <span className='inst-actions'>
                                          {postUrl ? (
                                            <a href={postUrl} target='_blank' rel='noopener noreferrer' className='inst-link-a'>
                                              {inst.is_xpost ? 'פתח בקבוצה ↗' : 'פתח פוסט ↗'}
                                            </a>
                                          ) : (
                                            <span style={{color: 'rgba(28,25,52,0.4)'}}>אין קישור</span>
                                          )}
                                          {isAdmin && postUrl && inst.profile_id && (
                                            <button
                                              type='button'
                                              title='תצוגה מקדימה של הפרופיל'
                                              onClick={(e) => {
                                                e.stopPropagation()
                                                // Sub-profile → open the PARENT's session and switch to the sub
                                                // (?as=<sub fb id>) before ?goto=. Parent profile → open directly.
                                                const sessionId = inst.profile_parent_id || inst.profile_id
                                                const switchTo = inst.profile_parent_id ? inst.profile_facebook_id : undefined
                                                openLivePreview(sessionId, postUrl, switchTo)
                                              }}
                                              style={{
                                                background: 'none',
                                                border: 'none',
                                                fontSize: '16px',
                                                cursor: 'pointer',
                                                padding: '2px 6px',
                                                borderRadius: '50%',
                                                color: '#5b6ef5',
                                                lineHeight: 1,
                                              }}
                                            >
                                              <i className='bi bi-display'></i>
                                            </button>
                                          )}
                                        </span>
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}

export {StatsWrapper}
